/**
 * Section: shpflt-collection-menu
 * Overflow detection and previous/next arrows for the subcategory menu.
 *
 * Swipe, scroll, snapping and centring are CSS (overflow-x + scroll-snap + safe center); smooth
 * scrolling comes from `scroll-behavior` in the stylesheet, which is off under
 * prefers-reduced-motion. CSS cannot tell whether a flex row overflows, so JS only:
 *   - toggles [data-overflowing] on the section (the stylesheet shows the arrows only then),
 *   - moves the list one page per arrow click,
 *   - keeps aria-disabled in sync at both ends.
 * Loaded only when there are at least two cards. No autoplay.
 */
import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * @typedef {Object} CollectionMenuRefs
 * @property {HTMLUListElement} list - Scrollable card list
 * @property {HTMLButtonElement} previous - "Previous" arrow
 * @property {HTMLButtonElement} next - "Next" arrow
 */

/**
 * ShpfltCollectionMenu — Subcategory menu that shows its arrows only when the row overflows.
 *
 * @example
 * <shpflt-collection-menu data-shpflt id="{{ section_id }}" data-section-id="{{ section.id }}">
 *   <button type="button" ref="previous" aria-controls="List" aria-disabled="true">...</button>
 *   <ul id="List" ref="list" role="list">...</ul>
 *   <button type="button" ref="next" aria-controls="List">...</button>
 * </shpflt-collection-menu>
 */
class ShpfltCollectionMenu extends ShopigurusHTMLElement {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { list, previous, next } = this.#getRefs();
    if (!list) return;

    const { signal } = this.abortController;
    previous?.addEventListener('click', this.#handleArrowClick, { signal });
    next?.addEventListener('click', this.#handleArrowClick, { signal });
    list.addEventListener('scroll', this.#update, { passive: true, signal });

    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(list);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  /**
   * Reads the component refs (not cached, see ShopigurusHTMLElement#refs).
   * @returns {Partial<CollectionMenuRefs>}
   */
  #getRefs() {
    return /** @type {Partial<CollectionMenuRefs>} */ (this.refs);
  }

  /**
   * Scrolls the list by one visible page in the clicked arrow's direction.
   * @listens {click}
   * @param {MouseEvent} event
   */
  #handleArrowClick = (event) => {
    const arrow = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const { list, next } = this.#getRefs();
    if (!list || arrow.getAttribute('aria-disabled') === 'true') return;

    const forward = arrow === next ? 1 : -1;
    const rtl = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
    list.scrollBy({ left: forward * rtl * list.clientWidth });
  };

  /**
   * Flags overflow on the section and marks the arrow at each end as disabled (it stays in the
   * tab order so focus is not lost when the list reaches an end).
   * @listens {scroll}
   */
  #update = () => {
    const { list, previous, next } = this.#getRefs();
    if (!list) return;

    const maxScroll = list.scrollWidth - list.clientWidth;
    this.toggleAttribute('data-overflowing', maxScroll > 1);

    const position = Math.abs(list.scrollLeft);
    previous?.setAttribute('aria-disabled', String(position <= 1));
    next?.setAttribute('aria-disabled', String(position >= maxScroll - 1));
  };
}

if (!customElements.get('shpflt-collection-menu')) {
  customElements.define('shpflt-collection-menu', ShpfltCollectionMenu);
}
